import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ScrollElements } from '@app/core/forms/directives/scroll-ements';
import { extractHttpErrorResponse } from '@app/core/helpers/extract-http-error-response';
import { AuthSelectors, SetReturnUrl, SignIn } from '@app/store';
// import {
//   SetIsWrongCredentials,
//   SignInViewSelectors,
// } from '@app/store/views/sign-in';
import { Navigate } from '@ngxs/router-plugin';
import {
  ActionCompletion,
  Actions,
  ofActionCompleted,
  Store,
} from '@ngxs/store';
import { tap } from 'rxjs';
import { ErrorsHostDirective } from 'src/app/core/forms/directives/errors-host.directive';
import { IfInvalidDirective } from 'src/app/core/forms/directives/if-invalid.directive';
import { ScrollToInvalidFieldOnFormSubmitDirective } from 'src/app/core/forms/directives/scroll-to-invalid-field-on-form-submit.directive';
import { SignInWithGoogleComponent } from './sign-in-with-google/sign-in-with-google.component';

@Component({
  selector: 'app-sign-in-page',
  templateUrl: './sign-in.page.html',
  styleUrls: ['./sign-in.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    ScrollToInvalidFieldOnFormSubmitDirective,
    IfInvalidDirective,
    ErrorsHostDirective,
    SignInWithGoogleComponent,
  ],
})
export class SignInPage implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    @Inject(PLATFORM_ID) private platformId: Object,
    private store: Store,
    private actions$: Actions,
    private destroyRef: DestroyRef,
    private cdRef: ChangeDetectorRef,
  ) {}

  protected returnUrl: string | undefined = inject(Store).selectSnapshot(
    AuthSelectors.returnUrl,
  );

  protected form!: FormGroup;
  protected formError?: string;

  protected ScrollElements = ScrollElements;

  ngOnInit(): void {
    this.form = this.buildForm();
    this.handleBackendResponse();
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      email: [null, Validators.required],
      password: [null, Validators.required],
    });
  }

  private handleBackendResponse(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofActionCompleted(SignIn),
        tap((actionCompletion: ActionCompletion) => {
          if (actionCompletion.result.successful) {
            this.store.dispatch(new SetReturnUrl(undefined));
            this.store.dispatch(new Navigate([this.returnUrl || '/account']));
          } else {
            const errors = extractHttpErrorResponse(actionCompletion);
            this.formError = errors.detail;
            this.cdRef.detectChanges();
            this.scrollToFormError();
          }
        }),
      )
      .subscribe();

    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.formError = undefined;
        }),
      )
      .subscribe();
  }

  get usernameFC(): FormControl {
    return this.form.get('email') as FormControl;
  }

  get passwordFC(): FormControl {
    return this.form.get('password') as FormControl;
  }

  private scrollToFormError(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const el: HTMLElement | null = document.querySelector('.system-error');
    if (!el) {
      return;
    }

    window.scroll({
      top: this.getTopOffset(el),
      left: 0,
      behavior: 'smooth',
    });
  }

  private getTopOffset(el: HTMLElement): number {
    const offsetTop = 20;
    const scrollTop = window.scrollY;

    return el.getBoundingClientRect().top + scrollTop - offsetTop;
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.store.dispatch(new SignIn(this.form.value));
  }
}
